import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip';
import React, { createContext, useContext, useState } from 'react';

import type { IconName, IconSize } from '../../assets/Icon/Icon';
import type { DynamicTextPropType } from '../../text/DynamicText';
import type { TextWeightProp } from '../../text/Text';
import type { AlignStackProps } from '../../utilities/AlignStack/AlignStack';
import type { ColorProps } from '../../utilities/shared/Color';
import { Icon } from '../../assets/Icon/Icon';
import {
  slideDownAndFade,
  slideLeftAndFade,
  slideRightAndFade,
  slideUpAndFade,
} from '../../common/animations';
import { type VariantProp, fade, palette } from '../../common/colors';
import { devices } from '../../common/devices';
import { focusRingPseudoElement } from '../../common/focus_rings';
import { VisuallyHidden } from '../../common/visually_hidden';
import { zIndexes } from '../../common/z_indexes';
import { selectors } from '../../controls/shared/styles';
import { colors, darkThemeSelector, keyframes, shadows, styled } from '../../stitches.config';
import { DynamicText } from '../../text/DynamicText';
import { Small } from '../../text/Small';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { useCanHover } from '../../utilities/getCanHover';

const TooltipTrigger = styled(BaseTooltip.Trigger, {
  [selectors.focus]: {
    outline: 'none',
  },

  variants: {
    fallback: {
      true: {
        position: 'relative',
        '&::before': focusRingPseudoElement('-$2', '$10'),

        // Not `selectors.focus`, which also matches a focused control inside that draws its
        // own ring. Menus focus this wrapper on open but mark focus with the item highlight.
        "&:focus-visible:not([role='menu'] *)": {
          zIndex: 2,

          '&::before': {
            boxShadow: shadows.focusRingLight,

            [darkThemeSelector]: {
              boxShadow: shadows.focusRingDark,
            },
          },
        },

        // Browsers suppress mouse events over disabled controls (Firefox entirely, Chrome
        // on the control itself), which starves Base UI's hover tracking — keep hit-testing
        // on this wrapper instead.
        '& :disabled': {
          pointerEvents: 'none',
        },

        // The control's own `not-allowed` can't show through `pointer-events: none`.
        "&:has(:disabled, [aria-disabled='true'])": {
          cursor: 'not-allowed',
        },
      },
      false: {},
    },
  },
});
TooltipTrigger.displayName = 'TooltipTrigger';

// Lays out like the `<button>` it replaces, so wrapped content doesn't shift.
const TooltipTriggerWrapper = styled('span', {
  display: 'inline-block',
  textAlign: 'center',
});
TooltipTriggerWrapper.displayName = 'TooltipTriggerWrapper';

const InsideTooltipTriggerContext = createContext(false);

/**
 * True inside an atto Tooltip trigger that will show contents. Controls that wrap
 * themselves in a fallback tooltip (e.g. hidden-label Button) use it to skip theirs,
 * which would otherwise race the ancestor's tooltip in the shared delay group.
 */
export function useInsideTooltipTrigger() {
  return useContext(InsideTooltipTriggerContext);
}

const tooltipGlowLight = keyframes({
  '0%, 100%': {
    boxShadow: `
      ${shadows.overlayAppLight},
      0 0 4px 0 ${fade(palette.brand600, 0.15)}
    `,
  },
  '50%': {
    boxShadow: `
      ${shadows.overlayAppLight},
      0 0 12px 0 ${fade(palette.brand600, 0.4)}
    `,
  },
});

const tooltipGlowDark = keyframes({
  '0%, 100%': {
    boxShadow: `
      ${shadows.overlayAppDark},
      0 0 4px 0 ${fade(palette.brand600, 0.15)}
    `,
  },
  '50%': {
    boxShadow: `
      ${shadows.overlayAppDark},
      0 0 12px 0 ${fade(palette.brand600, 0.4)}
    `,
  },
});

export const TooltipContainer = styled('div', Small, {
  display: 'inline-flex',
  width: 'fit-content',
  maxWidth: '$280',
  backgroundColor: colors.bgApplicationLight,
  boxShadow: shadows.overlayLight,
  borderRadius: '$8',
  color: colors.gray700,
  pointerEvents: 'none',
  userSelect: 'none',

  [darkThemeSelector]: {
    color: colors.gray50,
    backgroundColor: colors.bgApplicationDark,
    boxShadow: shadows.overlayDark,
  },

  [selectors.focus]: {
    outline: 'none',
  },

  variants: {
    hideOnDesktop: {
      true: {
        [devices.mouse]: {
          display: 'none',
        },
      },
      false: {},
    },
    pulse: {
      true: {
        animation: `${tooltipGlowLight} 1.5s ease-in-out infinite`,

        [darkThemeSelector]: {
          animation: `${tooltipGlowDark} 1.5s ease-in-out infinite`,
        },
      },
      false: {},
    },
    tight: {
      true: {},
      false: {
        padding: '$4 $6',
      },
    },
  },
});

const TooltipPositioner = styled(BaseTooltip.Positioner, {
  zIndex: zIndexes.tooltip,

  '&[data-anchor-hidden]': {
    visibility: 'hidden',
  },
});
TooltipPositioner.displayName = 'TooltipPositioner';

const TooltipPopup = styled(BaseTooltip.Popup, {
  display: 'contents',
  pointerEvents: 'none',
  userSelect: 'none',

  '@media (prefers-reduced-motion: no-preference)': {
    animationDuration: '150ms',
    animationTimingFunction: 'ease-out',
    animationFillMode: 'forwards',
    willChange: 'transform, opacity',

    '&[data-open]': {
      '&[data-side="top"]': { animationName: slideUpAndFade },
      '&[data-side="right"]': { animationName: slideLeftAndFade },
      '&[data-side="bottom"]': { animationName: slideDownAndFade },
      '&[data-side="left"]': { animationName: slideRightAndFade },
    },
  },
});
TooltipPopup.displayName = 'TooltipPopup';

export type TooltipSide = 'top' | 'right' | 'bottom' | 'left';

export interface TooltipProps {
  /**
   * Set whether the tooltip should wrap its children or be mounted to its children. When false,
   * the wrapper is a focusable span, so it can hold a control, such as a disabled one whose
   * reason the tooltip explains. A `triggerLabel` keeps it a button instead.
   */
  asChild?: boolean;
  /**
   * How the tooltip aligns to its trigger, defaults to center.
   */
  align?: 'center' | 'end' | 'start';
  /**
   * Positions the tooltip against this element instead of the trigger.
   */
  anchor?: BaseTooltip.Positioner.Props['anchor'];
  /**
   * Any child will be set as the trigger for the tooltip.
   */
  children?: React.ReactNode;
  /**
   * Whether clicking the trigger closes the tooltip, defaults to true. Ignored in toggle mode.
   */
  closeOnClick?: boolean;
  /**
   * The distance in pixels from the trigger, defaults to 6.
   */
  collisionPadding?: number;
  /**
   * Pass in any content to display inside the tooltip.
   */
  contents?: React.ReactNode;
  /**
   * The delay in milliseconds before the tooltip is shown, defaults to 100.
   */
  delay?: number;
  /**
   * Names the trigger, rendered visually hidden inside it. Required whenever the
   * tooltip has to supply its own trigger — an icon, diagram or image on its own
   * gives the resulting trigger no accessible name, so a screen reader has nothing
   * to announce. Setting it keeps that trigger a button, since a span can't be
   * named. Ignored when the child is used as the trigger, since it then names itself.
   */
  triggerLabel?: React.ReactNode;
  /**
   * Hides the tooltip for desktop users.
   */
  hideOnDesktop?: boolean;
  /**
   * If true, the tooltip will pulse when it is open.
   */
  pulse?: boolean;
  /**
   * The preferred side of the trigger to render against when open.
   */
  side?: TooltipSide;
  /**
   * The distance in pixels from the trigger, defaults to 6.
   */
  sideOffset?: number;
  /**
   * Treats the tooltip as a toggle, opening it (and closing it) when the trigger is clicked,
   * instead of on hover.
   */
  toggle?: boolean;
  /**
   * If true, remove padding from the tooltip container.
   */
  tight?: boolean;
  /**
   * If provided, controls the open state of the tooltip.
   */
  isOpen?: boolean;
  /**
   * Called when hovering, focusing or dismissing the trigger would open or close a tooltip
   * controlled by `isOpen`, so the caller can combine that interaction with its own reasons to
   * show it. Ignored unless `isOpen` is set.
   */
  onOpenChange?: BaseTooltip.Root.Props['onOpenChange'];
  /**
   * When true, the tooltip will not open.
   */
  disabled?: boolean;
  /**
   * Consulted as the tooltip is about to open; returning false keeps it closed. Lets CSS-driven
   * states (e.g. container-condensed buttons) gate the tooltip at open time.
   */
  shouldOpen?: () => boolean;
  /**
   * Leaves the app-wide delay group, so the tooltip waits its full delay even right after another
   * tooltip closes. Suits dense lists, where moving down the rows would otherwise open each one.
   */
  ungroupedDelay?: boolean;
}

export function Tooltip({
  align,
  anchor,
  asChild = true,
  children,
  closeOnClick = true,
  collisionPadding = 12,
  contents,
  delay = 100,
  hideOnDesktop,
  pulse = false,
  side,
  sideOffset = 6,
  toggle,
  tight = false,
  triggerLabel,
  isOpen,
  onOpenChange,
  disabled = false,
  shouldOpen,
  ungroupedDelay = false,
}: TooltipProps) {
  const isControlled = isOpen !== undefined;
  const [open, setOpen] = useState(false);
  const canHover = useCanHover();

  // Set toggle to true by default for mobile viewports, false for devices without hover media
  const shouldToggle = toggle ?? !canHover;

  const openProps: Pick<BaseTooltip.Root.Props, 'open' | 'onOpenChange'> = {};

  if (isControlled) {
    openProps.open = isOpen;
    openProps.onOpenChange = onOpenChange;
  } else if (shouldToggle) {
    openProps.open = open;
    openProps.onOpenChange = (_nextOpen, eventDetails) => {
      if (eventDetails.reason === 'outside-press' || eventDetails.reason === 'escape-key') {
        setOpen(false);
      }
    };
  } else if (shouldOpen) {
    openProps.open = open;
    openProps.onOpenChange = (nextOpen) => {
      setOpen(nextOpen && shouldOpen());
    };
  }

  // `asChild` is only a request: base-ui can mount onto the child solely when it is a
  // single element. Anything else (a string, an array, nothing at all) falls back to a
  // button of our own, which then has to carry the children and be named. An element
  // wrapped on purpose gets a focusable span instead, since it's often a (disabled)
  // control that can't sit inside a button — unless it has a `triggerLabel`, which only
  // a button can carry as its name.
  const childIsElement = React.isValidElement(children);
  const childIsTrigger = asChild && childIsElement;
  const wrapsElement = !asChild && childIsElement && !triggerLabel;
  let triggerRender: React.ReactElement | undefined;
  if (childIsTrigger) {
    triggerRender = children;
  } else if (wrapsElement) {
    triggerRender = <TooltipTriggerWrapper tabIndex={0} />;
  }

  const root = (
    <BaseTooltip.Root disabled={disabled} {...openProps}>
      <InsideTooltipTriggerContext.Provider value={Boolean(contents) && !disabled}>
        <TooltipTrigger
          closeOnClick={closeOnClick && !shouldToggle}
          delay={delay}
          fallback={!childIsTrigger}
          onClick={
            shouldToggle && !isControlled
              ? () => setOpen((value) => !value && (shouldOpen ? shouldOpen() : true))
              : undefined
          }
          render={triggerRender}
          type={triggerRender ? undefined : 'button'}
        >
          {childIsTrigger ? null : (
            <>
              {children}
              {triggerLabel ? <VisuallyHidden>{triggerLabel}</VisuallyHidden> : null}
            </>
          )}
        </TooltipTrigger>
      </InsideTooltipTriggerContext.Provider>
      <BaseTooltip.Portal>
        <TooltipPositioner
          align={align}
          anchor={anchor}
          collisionPadding={collisionPadding}
          side={side}
          sideOffset={sideOffset}
        >
          <TooltipPopup>
            {contents ? (
              <TooltipContainer hideOnDesktop={hideOnDesktop} pulse={pulse} tight={tight}>
                {contents}
              </TooltipContainer>
            ) : null}
          </TooltipPopup>
        </TooltipPositioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  );

  return ungroupedDelay ? <BaseTooltip.Provider timeout={0}>{root}</BaseTooltip.Provider> : root;
}

const IconTooltipIcon = styled(Icon);
IconTooltipIcon.displayName = 'IconTooltipIcon';

const IconTooltipContainer = styled('div', {
  display: 'flex',
});
IconTooltipContainer.displayName = 'IconTooltipContainer';

export type IconTooltipProps = TooltipProps & {
  /**
   * The icon to display in the tooltip.
   */
  icon?: IconName;
  /**
   * Whether the tooltip is internal.
   */
  internal?: boolean;
  /**
   * The size of the icon.
   */
  size?: IconSize;
  /**
   * The variant of the icon.
   */
  variant?: VariantProp | 'inherit';
  /**
   * The color of the icon
   */
  color?: ColorProps;
};

export function IconTooltip({
  internal = false,
  icon = 'question',
  size = 14,
  variant = 'neutral',
  color,
  ...remaining
}: IconTooltipProps) {
  const { contents } = remaining;

  return (
    <Tooltip {...remaining}>
      <IconTooltipContainer>
        <IconTooltipIcon
          icon={icon}
          internal={internal}
          size={size}
          variant={variant}
          color={color}
        />
        <VisuallyHidden>{contents}</VisuallyHidden>
      </IconTooltipContainer>
    </Tooltip>
  );
}

export type LabelTooltipProps = Omit<IconTooltipProps, 'variant'> & {
  /**
   * The context of the text, whether it's a heading or body text.
   */
  context?: 'body' | 'heading';
  /**
   * The preset of the text, whether it's a caption or body text.
   */
  preset?: DynamicTextPropType;
  /**
   * Whether the text is internal.
   */
  internal?: boolean;
  /**
   * Whether the text should be truncated.
   */
  truncate?: boolean;
  /**
   * The width of the text.
   */
  width?: AlignStackProps['width'];
  /**
   * The variant of the tooltip and icon.
   */
  variant?: VariantProp;
  /**
   * The weight of the text.
   */
  weight?: TextWeightProp;
};

export function LabelTooltip({
  contents,
  context = 'body',
  children,
  icon = 'question',
  internal = false,
  size = 14,
  preset = 'body',
  truncate = true,
  variant = 'neutral',
  weight,
  width = 'fit-content',
  ...remaining
}: LabelTooltipProps) {
  const renderLabel = (
    <AlignStack
      align="center"
      direction="row"
      gap={4}
      preset={preset}
      width={width}
      end={
        contents ? (
          <IconTooltipIcon icon={icon} internal={internal} size={size} variant={variant} />
        ) : undefined
      }
    >
      <DynamicText
        context={context}
        type={preset}
        variant={variant}
        weight={weight}
        truncate={truncate}
        internal={internal}
      >
        {children}
      </DynamicText>
    </AlignStack>
  );

  if (contents) {
    return (
      <Tooltip contents={contents} {...remaining}>
        {renderLabel}
      </Tooltip>
    );
  }

  return renderLabel;
}
